// Comprime un'immagine lato client prima dell'invio (evita upload pesanti e
// l'errore 413). Usa Canvas per ridimensionare al massimo a `maxDim` px per lato
// e ricodificare in JPEG con la qualità richiesta. Ritorna un File JPEG.
export async function compressImage(
  file: File,
  opts: { maxDim?: number; quality?: number } = {},
): Promise<File> {
  const maxDim = opts.maxDim ?? 1024;
  const quality = opts.quality ?? 0.70;

  // Se non è un'immagine, ritorna il file originale senza toccarlo
  if (!file.type.startsWith('image/')) return file;

  try {
    // Metodo 1: createImageBitmap (veloce, ma non decodifica HEIC su alcuni browser)
    const bitmap = await createImageBitmap(file);
    const out = await drawResized(bitmap, maxDim, quality, file.name);
    bitmap.close?.();
    return out;
  } catch {
    // Metodo 2: <img> + decode() (gestisce più formati, incl. alcuni HEIC)
    try {
      const out = await decodeViaImg(file, maxDim, quality);
      if (out) return out;
    } catch {
      /* ignora e passa all'originale */
    }
    // Fallback: se proprio non è decodificabile (es. HEIC non supportato),
    // invia l'originale (il backend lo accetta). Meglio non perdere la foto.
    return file;
  }
}

// Decodifica tramite elemento <img> e lo disegna su canvas ridimensionato
async function decodeViaImg(
  file: File,
  maxDim: number,
  quality: number,
): Promise<File | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      try {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, w, h);
        const blob: Blob | null = await new Promise((r) =>
          canvas.toBlob(r, 'image/jpeg', quality),
        );
        const base = (file.name || 'foto').replace(/\.[^.]+$/, '');
        resolve(blob ? new File([blob], `${base || 'foto'}.jpg`, { type: 'image/jpeg' }) : null);
      } catch {
        resolve(null);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

// Disegna il bitmap su canvas ridimensionato e ritorna un File JPEG
async function drawResized(
  bitmap: ImageBitmap,
  maxDim: number,
  quality: number,
  originalName: string,
): Promise<File> {
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas non disponibile');
  ctx.drawImage(bitmap, 0, 0, w, h);
  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality),
  );
  if (!blob) throw new Error('toBlob fallito');
  const base = (originalName || 'foto').replace(/\.[^.]+$/, '');
  return new File([blob], `${base || 'foto'}.jpg`, { type: 'image/jpeg' });
}

// Comprime più file in parallelo
export async function compressImages(files: File[]): Promise<File[]> {
  return Promise.all(files.map((f) => compressImage(f)));
}
